import { acpAgentCatalogEntryForAlias, acpAgentCatalogEntryForId } from '@crowdex/agent-contracts';
import { Badge } from '@crowdex/ui/components/badge';
import { Button } from '@crowdex/ui/components/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@crowdex/ui/components/collapsible';
import { Input } from '@crowdex/ui/components/input';
import { cn } from '@crowdex/ui/lib/utils';
import {
  ChevronDown,
  CircleAlert,
  LoaderCircle,
  Monitor,
  Plus,
  RefreshCw,
  RotateCcw,
  X,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { AgentLogo } from '@/components/agent-logo';
import {
  acpAgentPreferenceId,
  registryIdForLocalAgent,
} from '@/lib/agent-runtime/acp-agent-catalog';
import {
  detectedAgentLaunchCommand,
  formatAgentLaunchCommand,
} from '@/lib/agent-runtime/agent-runtime-config';
import type { AgentRuntimeOverride, AppSettings } from '@/lib/app-settings';
import type { NativeAgent } from '@/lib/native-desktop';
import { useRouter } from '@/lib/navigation';
import { pluginsPath } from '@/lib/plugins/plugin-sections';
import { AgentModelSettings } from './agent-model-settings';
import { SettingsGroup, settingButtonClass } from './setting-controls';
import { useAgentSettingsPanel } from './use-agent-settings-panel';

/** How many agents "Needs setup" shows before "Show more". */
const SETUP_SHOWN = 4;

/** The value `defaultAgent` keeps for an agent: its alias (`claude`), else its id. */
export function defaultAgentValue(agentId: string): string {
  return acpAgentCatalogEntryForId(agentId)?.aliases[0] ?? agentId;
}

/** Whether new tasks start with this agent; Automatic starts with Claude Code. */
export function isDefaultAgent(defaultAgent: string, agentId: string): boolean {
  if (defaultAgent === 'auto') return agentId === 'claude-acp';
  const entry =
    acpAgentCatalogEntryForAlias(defaultAgent) ?? acpAgentCatalogEntryForId(defaultAgent);
  return (entry?.id ?? defaultAgent) === agentId;
}

function AgentTile({ id, name, icon }: { id: string; name: string; icon?: string | null }) {
  return (
    <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-control text-foreground">
      <AgentLogo className="size-4" iconUrl={icon ?? undefined} id={id} name={name} />
    </span>
  );
}

function AgentRow({
  logo,
  name,
  detail,
  children,
  below,
  className,
}: {
  logo: ReactNode;
  name: string;
  detail: ReactNode;
  children: ReactNode;
  below?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('border-hairline border-b px-4 py-3 last:border-b-0', className)}>
      {/* A narrow phone puts the buttons under the name rather than cut it to a few letters. */}
      <div className="flex items-center gap-3 max-sm:flex-wrap">
        {logo}
        <div className="min-w-0 flex-1 max-sm:basis-28">
          <p className="truncate text-foreground text-sm max-md:text-base">{name}</p>
          <div className="mt-0.5 truncate text-muted-foreground text-xs max-md:text-sm">
            {detail}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2 max-sm:ml-auto">{children}</div>
      </div>
      {below}
    </div>
  );
}

/**
 * Settings › Agents (D18, phone 16): the agents ready to start tasks, the default one marked and
 * "Make default" on the others; those that need setup, "Connect" on their row.
 */
export function AgentSettingsPanel({
  agentIds,
  agentRuntimeOverrides,
  defaultAgent,
  onAgentIdsChange,
  onAgentRuntimeOverridesChange,
  onDefaultAgentChange,
}: {
  agentIds: AppSettings['agentIds'];
  agentRuntimeOverrides: AppSettings['agentRuntimeOverrides'];
  defaultAgent: string;
  onAgentIdsChange: (value: AppSettings['agentIds']) => void;
  onAgentRuntimeOverridesChange: (value: AppSettings['agentRuntimeOverrides']) => void;
  onDefaultAgentChange: (value: string) => void;
}) {
  const router = useRouter();
  const {
    catalogAgents,
    catalogError,
    desktop,
    error,
    expanded,
    loaded,
    onDemandAgents,
    refresh,
    runtimeAgents,
    selectedAgentCount,
    selectedIds,
    setShowAllAgents,
    showAllAgents,
    toggleExpanded,
    updateAgentSelection,
    updateRuntimeOverride,
  } = useAgentSettingsPanel({
    agentIds,
    agentRuntimeOverrides,
    onAgentIdsChange,
    onAgentRuntimeOverridesChange,
  });

  if (desktop === null || (desktop && !loaded)) {
    return (
      <p className="flex items-center gap-2 text-muted-foreground text-sm">
        <LoaderCircle className="size-4 animate-spin" /> Loading agents
      </p>
    );
  }

  if (!desktop) {
    return (
      <section className="rounded-2xl border bg-card px-6 py-10 text-center">
        <Monitor className="mx-auto size-6 text-muted-foreground" />
        <h3 className="mt-3 font-medium text-sm">Open Crowdex Desktop</h3>
        <p className="mt-1 text-muted-foreground text-xs">
          Agents run on your computer: their configuration is kept there.
        </p>
      </section>
    );
  }

  const readyRuntime = runtimeAgents.filter((agent) =>
    selectedIds.has(acpAgentPreferenceId(agent)),
  );
  const setupRuntime = runtimeAgents.filter(
    (agent) => !selectedIds.has(acpAgentPreferenceId(agent)),
  );
  const readyOnDemand = onDemandAgents.filter((agent) => selectedIds.has(agent.id));
  const setupOnDemand = onDemandAgents.filter((agent) => !selectedIds.has(agent.id));
  const setupCount = setupRuntime.length + setupOnDemand.length;
  const shownSetupOnDemand = showAllAgents
    ? setupOnDemand
    : setupOnDemand.slice(0, Math.max(0, SETUP_SHOWN - setupRuntime.length));
  const hiddenSetup = setupOnDemand.length - shownSetupOnDemand.length;

  const defaultControl = (agentId: string, name: string) =>
    isDefaultAgent(defaultAgent, agentId) ? (
      <Badge size="sm">Default</Badge>
    ) : (
      <button
        aria-label={`Make ${name} the default agent`}
        className={settingButtonClass()}
        onClick={() => onDefaultAgentChange(defaultAgentValue(agentId))}
        type="button"
      >
        Make default
      </button>
    );
  const connect = (preferenceId: string, name: string, runtimeId?: string) => (
    <button
      aria-label={`Connect ${name}`}
      // Outline pills: several agents can need setup at once (one filled button per screen).
      className={settingButtonClass()}
      onClick={() => {
        updateAgentSelection(preferenceId, true);
        if (runtimeId !== undefined && !expanded.has(runtimeId)) toggleExpanded(runtimeId);
      }}
      type="button"
    >
      Connect
    </button>
  );

  return (
    <div>
      {error && (
        <div className="mb-5 flex items-center gap-3 rounded-2xl bg-destructive/10 px-4 py-3 text-destructive text-sm">
          <CircleAlert className="size-4 shrink-0" />
          <span className="min-w-0 flex-1">{error}</span>
          <Button onClick={() => void refresh()} size="sm" variant="ghost">
            <RefreshCw className="size-3.5" /> Retry
          </Button>
        </div>
      )}
      <SettingsGroup title={`Ready · ${readyRuntime.length + readyOnDemand.length}`}>
        {readyRuntime.map((runtimeAgent) => {
          const registryId = registryIdForLocalAgent(runtimeAgent);
          const agentId = registryId ?? runtimeAgent.id;
          const catalogAgent = catalogAgents.find((agent) => agent.id === registryId);
          const preferenceId = acpAgentPreferenceId(runtimeAgent);
          const open = expanded.has(runtimeAgent.id);
          return (
            <Collapsible
              // The divider is the wrapper's: the row is always its last child.
              className="border-hairline border-b last:border-b-0"
              key={runtimeAgent.id}
              onOpenChange={() => toggleExpanded(runtimeAgent.id)}
              open={open}
            >
              <AgentRow
                below={
                  <CollapsibleContent>
                    {registryId !== null && <AgentModelSettings agentId={registryId} />}
                    <RuntimeSettings
                      agentId={runtimeAgent.id}
                      detected={runtimeAgent}
                      onChange={(value) => updateRuntimeOverride(runtimeAgent.id, value)}
                      override={agentRuntimeOverrides[runtimeAgent.id]}
                    />
                    <div className="mt-4 flex justify-end">
                      <button
                        className={settingButtonClass()}
                        disabled={selectedAgentCount === 1}
                        onClick={() => updateAgentSelection(preferenceId, false)}
                        type="button"
                      >
                        Stop using {runtimeAgent.name}
                      </button>
                    </div>
                  </CollapsibleContent>
                }
                className="border-b-0"
                detail={
                  <span className="font-mono">{detectedAgentLaunchCommand(runtimeAgent)}</span>
                }
                logo={
                  <AgentTile
                    icon={catalogAgent?.icon}
                    id={runtimeAgent.id}
                    name={runtimeAgent.name}
                  />
                }
                name={runtimeAgent.name}
              >
                {defaultControl(agentId, runtimeAgent.name)}
                {/* A phone keeps the chevron only: "Make default" takes the row's room. */}
                <CollapsibleTrigger
                  aria-label={`Configure ${runtimeAgent.name}`}
                  className={cn(settingButtonClass('control'), 'max-md:px-2')}
                >
                  <span className="max-md:hidden">Configure</span>
                  <ChevronDown
                    className={cn('size-3.5 transition-transform', open && 'rotate-180')}
                  />
                </CollapsibleTrigger>
              </AgentRow>
            </Collapsible>
          );
        })}
        {readyOnDemand.map((agent) => (
          <AgentRow
            detail={agent.description || `Installed when a task needs it · v${agent.version}`}
            key={agent.id}
            logo={<AgentTile icon={agent.icon} id={agent.id} name={agent.name} />}
            name={agent.name}
          >
            {defaultControl(agent.id, agent.name)}
            <button
              aria-label={`Stop using ${agent.name}`}
              className={cn(settingButtonClass('control'), 'max-md:px-2')}
              disabled={selectedAgentCount === 1}
              onClick={() => updateAgentSelection(agent.id, false)}
              type="button"
            >
              <X aria-hidden className="size-3.5 md:hidden" />
              <span className="max-md:hidden">Remove</span>
            </button>
          </AgentRow>
        ))}
        {readyRuntime.length + readyOnDemand.length === 0 && (
          <p className="px-4 py-4 text-muted-foreground text-sm">
            No agent is ready yet: connect one below.
          </p>
        )}
      </SettingsGroup>
      {setupCount > 0 && (
        <SettingsGroup title={`Needs setup · ${setupCount}`}>
          {setupRuntime.map((agent) => (
            <AgentRow
              detail="Found on this computer"
              key={agent.id}
              logo={<AgentTile id={agent.id} name={agent.name} />}
              name={agent.name}
            >
              {connect(acpAgentPreferenceId(agent), agent.name, agent.id)}
            </AgentRow>
          ))}
          {shownSetupOnDemand.map((agent) => (
            <AgentRow
              detail={agent.description || 'Installed when a task needs it'}
              key={agent.id}
              logo={<AgentTile icon={agent.icon} id={agent.id} name={agent.name} />}
              name={agent.name}
            >
              {connect(agent.id, agent.name)}
            </AgentRow>
          ))}
          {(hiddenSetup > 0 || showAllAgents) && setupOnDemand.length > SETUP_SHOWN && (
            <button
              className="w-full px-4 py-2.5 text-left text-muted-foreground text-sm hover:text-foreground"
              onClick={() => setShowAllAgents((current) => !current)}
              type="button"
            >
              {showAllAgents ? 'Show less' : `Show ${hiddenSetup} more`}
            </button>
          )}
        </SettingsGroup>
      )}
      {catalogError && (
        <p className="-mt-4 mb-5 flex items-center gap-1.5 px-1 text-destructive text-xs">
          <CircleAlert className="size-3" /> Registry unavailable: {catalogError}
        </p>
      )}
      <button
        className="text-foreground text-sm underline underline-offset-2 max-md:flex max-md:h-12 max-md:w-full max-md:items-center max-md:justify-center max-md:gap-2 max-md:rounded-2xl max-md:border max-md:border-foreground/20 max-md:border-dashed max-md:no-underline"
        onClick={() => router.push(pluginsPath())}
        type="button"
      >
        <Plus aria-hidden className="hidden size-4 max-md:block" />
        Add an agent from Plugins
      </button>
    </div>
  );
}

function RuntimeSettings({
  agentId,
  detected,
  onChange,
  override,
}: {
  agentId: string;
  detected?: NativeAgent;
  onChange: (value?: AgentRuntimeOverride) => void;
  override?: AgentRuntimeOverride;
}) {
  const launchCommand =
    override?.launchCommand ??
    (override?.command
      ? formatAgentLaunchCommand(override.command, override.args ?? detected?.args ?? [])
      : detected
        ? detectedAgentLaunchCommand(detected)
        : '');

  return (
    <div className="mt-4 space-y-3 border-hairline border-t pt-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="font-medium text-xs">Local launch</p>
          <p className="mt-0.5 text-muted-foreground text-caption">
            The command Crowdex starts this agent with.
          </p>
        </div>
        {override !== undefined && (
          <Button className="gap-1.5" onClick={() => onChange()} size="xs" variant="ghost">
            <RotateCcw className="size-3" /> Default command
          </Button>
        )}
      </div>
      <label className="block space-y-1.5" htmlFor={`agent-command-${agentId}`}>
        <span className="sr-only">Command</span>
        <Input
          className="font-mono text-xs"
          id={`agent-command-${agentId}`}
          onChange={(event) =>
            onChange({
              ...override,
              args: undefined,
              command: undefined,
              launchCommand: event.target.value,
            })
          }
          placeholder="Agent executable and arguments"
          value={launchCommand}
        />
      </label>
    </div>
  );
}
